import { useState } from 'react'
import { ArrowLeftIcon } from './icons.jsx'
import Select from './Select.jsx'

// Reusable split-screen multi-step wizard (new lead, invite team, any
// flow with several steps). Left = visual aside; right = an optional "back to X"
// link, a segmented progress bar, the current step's title/description/fields,
// and Back/Continue.
//
// Each step is either declarative (a `fields` array) or fully custom (a
// `render(data, setField)` function). Fields:
//   { name, label, type: 'text'|'email'|'select'|'textarea', width: 'full'|'half',
//     placeholder, options: [str | {value,label}], required, requiredMessage, rows }
// Layout-only rows: { type: 'help', text } and { type: 'divider' }.

const FIELD_BASE =
  'w-full rounded-[10px] border bg-panel px-[14px] py-[12px] text-[14px] text-ink transition-[border-color,box-shadow] duration-150 placeholder:text-faint focus:outline-none focus:border-primary focus:shadow-[0_0_0_3px_rgba(60,135,247,0.2)]'

function normalizeOptions(options = []) {
  return options.map((o) => (typeof o === 'string' ? { value: o, label: o } : o))
}

function Field({ item, value, onChange, error }) {
  if (item.type === 'divider') return <div className="col-span-2 my-[6px] h-px bg-line" />
  if (item.type === 'help')
    return <p className="col-span-2 m-0 text-[12.5px] leading-[1.5] text-dim">{item.text}</p>

  const id = `msf-${item.name}`
  const borderCls = error ? 'border-danger' : 'border-line'

  return (
    <div
      className={`flex min-w-0 flex-col gap-y-2 ${item.width === 'half' ? '' : 'col-span-2'}`}
    >
      <label className="text-[13px] font-medium text-ink" htmlFor={id}>
        {item.label}
      </label>

      {item.type === 'select' ? (
        <Select
          value={value ?? ''}
          onChange={(v) => onChange(item.name, v)}
          placeholder={item.placeholder || 'Select…'}
          ariaLabel={item.label}
          buttonClassName={`w-full !rounded-[10px] !bg-panel !px-[14px] !py-[12px] ${
            error ? '!border-danger' : ''
          }`}
          options={normalizeOptions(item.options)}
        />
      ) : item.type === 'textarea' ? (
        <textarea
          id={id}
          className={`${FIELD_BASE} ${borderCls} min-h-[96px] resize-y font-sora leading-[1.5]`}
          rows={item.rows || 4}
          placeholder={item.placeholder}
          value={value ?? ''}
          onChange={(e) => onChange(item.name, e.target.value)}
        />
      ) : (
        <input
          id={id}
          className={`${FIELD_BASE} ${borderCls}`}
          type={item.type || 'text'}
          placeholder={item.placeholder}
          value={value ?? ''}
          onChange={(e) => onChange(item.name, e.target.value)}
        />
      )}

      {error && <span className="text-[12px] text-danger">{error}</span>}
    </div>
  )
}

export default function MultiStepForm({
  steps,
  aside = null,
  initialData = {},
  finalLabel = 'Continue',
  onComplete,
  onExit,
  backLabel = 'Back',
}) {
  const [index, setIndex] = useState(0)
  const [data, setData] = useState(initialData)
  const [errors, setErrors] = useState({})
  // Custom `render` steps have no `fields` to validate, so they can declare
  // canContinue(data) → true | 'why not'. Without this a step with nothing
  // selected walks straight to the end and the final action no-ops silently.
  const [stepError, setStepError] = useState('')

  const step = steps[index]
  const isLast = index === steps.length - 1

  function setField(name, value) {
    setData((d) => ({ ...d, [name]: value }))
    // Clear a field's error as soon as the user edits it.
    setErrors((e) => {
      if (!e[name]) return e
      const next = { ...e }
      delete next[name]
      return next
    })
  }

  function validate() {
    const errs = {}
    for (const item of step.fields || []) {
      if (item.required && !String(data[item.name] ?? '').trim()) {
        errs[item.name] = item.requiredMessage || 'This field is required'
      }
    }
    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  function next() {
    if (step.fields && !validate()) return
    if (step.canContinue) {
      const ok = step.canContinue(data)
      if (ok !== true) {
        setStepError(typeof ok === 'string' ? ok : 'Finish this step to continue')
        return
      }
    }
    setStepError('')
    if (isLast) {
      // A completion handler can refuse with a message (sync or async) instead
      // of failing quietly.
      const res = onComplete?.(data)
      if (res && typeof res.then === 'function') {
        res.then((r) => {
          if (typeof r === 'string' && r) setStepError(r)
        })
      } else if (typeof res === 'string' && res) {
        setStepError(res)
      }
      return
    }
    setIndex((i) => Math.min(i + 1, steps.length - 1))
  }

  function back() {
    setErrors({})
    setStepError('')
    setIndex((i) => Math.max(i - 1, 0))
  }

  // Enter advances the wizard (Continue, or the final action on the last step).
  // Textareas keep Enter for newlines; buttons/links/selects handle their own.
  function onKeyDown(e) {
    if (e.key !== 'Enter' || e.shiftKey || e.metaKey || e.ctrlKey || e.altKey) return
    const el = e.target
    const tag = (el.tagName || '').toLowerCase()
    if (tag === 'textarea' || tag === 'button' || tag === 'a' || tag === 'select') return
    if (el.isContentEditable) return
    e.preventDefault()
    next()
  }

  return (
    // h-full (not min-h-screen): the wizard lives inside the Shell card, which
    // is already viewport-height minus padding — a screen-height child would
    // always overflow it by that padding and grow a scrollbar.
    <div className="flex h-full bg-surface font-sora text-ink" onKeyDown={onKeyDown}>
      {aside}

      <div className="flex min-w-0 flex-1 basis-1/2 flex-col px-[44px] pb-[30px] pt-[26px]">
        <header className="flex min-h-[34px] items-center justify-between">
          {onExit && (
            <button
              className="inline-flex items-center gap-x-2 text-[13px] font-medium text-dim transition-colors hover:text-ink"
              type="button"
              onClick={onExit}
            >
              <ArrowLeftIcon className="h-4 w-4" />
              <span>{backLabel}</span>
            </button>
          )}
        </header>

        <div
          className="mx-auto mt-[10px] flex w-[210px] gap-x-[7px]"
          role="progressbar"
          aria-valuenow={index + 1}
          aria-valuemin={1}
          aria-valuemax={steps.length}
          aria-label={`Step ${index + 1} of ${steps.length}`}
        >
          {steps.map((s, i) => (
            <span key={s.id || i} className="relative h-[4px] flex-1 overflow-hidden rounded-[2px] bg-line">
              {/* Reached segments grow their fill left → right. */}
              <span
                className={`absolute inset-0 origin-left rounded-[2px] bg-ink transition-transform duration-500 ease-out ${
                  i <= index ? 'scale-x-100' : 'scale-x-0'
                }`}
              />
            </span>
          ))}
        </div>

        <div className="flex flex-1 flex-col justify-center overflow-y-auto py-8">
          <div className="mx-auto w-full max-w-[560px]">
            <h1 className="m-0 mb-[14px] text-[27px] font-semibold tracking-[-0.01em] text-ink">
              {step.title}
            </h1>
            {step.description && (
              <p className="m-0 max-w-[54ch] text-[14px] leading-[1.55] text-dim">
                {step.description}
              </p>
            )}

            {step.render ? (
              step.render(data, setField)
            ) : (
              <div className="mt-[30px] grid grid-cols-2 gap-x-[22px] gap-y-5">
                {step.fields.map((item, i) => (
                  <Field
                    key={item.name || `${item.type}-${i}`}
                    item={item}
                    value={data[item.name]}
                    onChange={setField}
                    error={errors[item.name]}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {stepError && (
          <div className="mx-auto w-full max-w-[560px] pt-4">
            <div className="rounded-[10px] bg-[#fef2f2] px-3.5 py-2.5 text-[13px] font-medium text-[#b91c1c] dark:bg-[rgba(220,38,38,0.16)] dark:text-[#fca5a5]">
              {stepError}
            </div>
          </div>
        )}

        <footer className="mx-auto flex w-full max-w-[560px] items-center justify-between pt-4">
          {index > 0 ? (
            <button
              className="inline-flex items-center rounded-[8px] border border-line px-[14px] py-[7px] text-[13px] font-semibold text-dim transition-colors hover:bg-panel hover:text-ink"
              onClick={back}
              type="button"
            >
              Back
            </button>
          ) : (
            <span />
          )}
          <button
            className="inline-flex min-w-[120px] items-center justify-center gap-x-1.5 rounded-[9px] bg-primary px-[18px] py-[11px] text-[15px] font-semibold text-white transition-colors hover:bg-primary-dark"
            onClick={next}
            type="button"
          >
            {isLast ? finalLabel : 'Continue'}
          </button>
        </footer>
      </div>
    </div>
  )
}
